import { isRoutePreloaded, preloadRouteChunk } from '@client/utils/route-preloader';
import type { AnyRoute } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

/** Idle timeout before starting preload (15 seconds) */
const PRELOAD_IDLE_TIMEOUT = 15000;

/** Delay between preloading each route chunk (100ms) */
const PRELOAD_DELAY = 100;

/**
 * Grace period after `window.load` (or hook mount, whichever comes later)
 * before background preloading is allowed. Gives the browser time to finish
 * post-load work (LCP candidate updates, deferred image decoding, late-arriving
 * fonts) without competing for bandwidth from the idle preloader.
 */
export const POST_LOAD_GRACE_MS = 1500;

/**
 * Returns `true` once two conditions are both satisfied:
 *   1. The page has fully loaded (`window.load` has fired, equivalent to
 *      `document.readyState === 'complete'`).
 *   2. A {@link POST_LOAD_GRACE_MS} grace period has elapsed.
 *
 * The reference point for the grace period is whichever of `load` or
 * hook-mount happens **later**:
 *   - If the page is still loading at mount → grace starts at `load` event.
 *   - If the page is already loaded at mount (typical when this hook is
 *     mounted by `AuthenticatedLayout` after auth, well after `load`) →
 *     grace starts at mount. Using mount-time as the reference is intentional:
 *     a fixed window from mount is sufficient breathing room and avoids
 *     tracking historical `loadEventEnd` timings.
 *
 * Used to gate background preloading so it cannot fire mid-pageload for slow
 * p75 users (where the `react-idle-timer` "idle" state can trigger while the
 * user is still waiting for the page to render).
 *
 * Exported for unit testing — not intended as a general-purpose API.
 */
export function usePageSettled(): boolean {
    const [settled, setSettled] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        const scheduleSettled = () => {
            timer = window.setTimeout(() => setSettled(true), POST_LOAD_GRACE_MS);
        };

        if (document.readyState === 'complete') {
            scheduleSettled();
            return () => {
                if (timer !== undefined) window.clearTimeout(timer);
            };
        }

        const onLoad = () => scheduleSettled();
        window.addEventListener('load', onLoad, { once: true });
        return () => {
            window.removeEventListener('load', onLoad);
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, []);

    return settled;
}

/**
 * Recursively collects all routes from the route tree that have components.
 * Prioritises shallower routes (more commonly accessed) first.
 */
function collectRoutesWithComponents(route: AnyRoute, depth = 0): Array<{ route: AnyRoute; depth: number }> {
    const results: Array<{ route: AnyRoute; depth: number }> = [];

    // Only include routes that have a component (lazy loaded)
    if (route.options?.component) {
        results.push({ route, depth });
    }

    // Recursively collect child routes
    const children = route.children as Record<string, AnyRoute> | undefined;
    if (children && typeof children === 'object') {
        for (const child of Object.values(children)) {
            results.push(...collectRoutesWithComponents(child, depth + 1));
        }
    }

    return results;
}

/**
 * Hook that preloads route chunks when the user is idle.
 *
 * Uses an idle timeout to detect when the user stops interacting with the page,
 * then progressively preloads route chunks in the background. The timeout is
 * deliberately longer than the initial LCP window because route preloading can
 * fan out into many chunks.
 * Preloading is paused when the user becomes active again.
 *
 * This improves perceived performance by loading route chunks before the user
 * navigates to them, reducing the delay when clicking links.
 *
 * @example
 * function AuthenticatedLayout({ children }) {
 *   // Enable idle preloading for authenticated users
 *   useIdlePreloader();
 *
 *   return <>{children}</>;
 * }
 */
export function useIdlePreloader() {
    const router = useRouter();
    const pageSettled = usePageSettled();

    const [isIdle, setIsIdle] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Separate idle timer with short timeout for preloading
    useIdleTimer({
        timeout: PRELOAD_IDLE_TIMEOUT,
        onIdle: () => setIsIdle(true),
        onActive: () => setIsIdle(false),
        throttle: 500,
        // Don't sync across tabs - each tab preloads independently
        crossTab: false,
    });

    const preloadRoutes = useCallback(async () => {
        // Create abort controller for this preload session
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        // Get the root route and collect all routes with components
        const rootRoute = router.routeTree;
        const allRoutes = collectRoutesWithComponents(rootRoute);

        // Sort by depth (shallower routes first - more commonly accessed)
        allRoutes.sort((a, b) => a.depth - b.depth);

        for (const { route } of allRoutes) {
            // Check if aborted (user became active)
            if (signal.aborted) {
                return;
            }

            // Skip already preloaded routes (checked globally in route-preloader.ts)
            if (isRoutePreloaded(route.id)) {
                continue;
            }

            try {
                // Use requestIdleCallback if available to avoid blocking main thread
                await new Promise<void>((resolve) => {
                    const callback = async () => {
                        if (!signal.aborted) {
                            await preloadRouteChunk(route, router);
                        }
                        resolve();
                    };

                    if ('requestIdleCallback' in window) {
                        window.requestIdleCallback(() => void callback(), { timeout: 1000 });
                    } else {
                        // Fallback for browsers without requestIdleCallback
                        setTimeout(() => void callback(), PRELOAD_DELAY);
                    }
                });

                // Small delay between routes to avoid network congestion
                await new Promise((resolve) => setTimeout(resolve, PRELOAD_DELAY));
            } catch {
                // Silently ignore errors - preloading is a performance optimisation
            }
        }
    }, [router]);

    useEffect(() => {
        // Gate background preloading on BOTH idle AND the page being settled.
        // Without the settled gate, a slow p75 page-load is interpreted by
        // `react-idle-timer` as "user is idle" (no input events), so the
        // preloader fires mid-pageload and steals bandwidth from LCP assets.
        if (isIdle && pageSettled) {
            void preloadRoutes();
        } else {
            // Cancel ongoing preloading when user becomes active
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        }

        return () => {
            // Cleanup on unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [isIdle, pageSettled, preloadRoutes]);
}
